/* Advent (owner request 2026-10-13: "We LOVE Christmas here"), the pure part: the wreath's Sundays, the countdown, which
   calendar doors may be opened, the growing snow, the Christmas Eve sleigh, and the 24 doors. Dates are the person's own
   days ('YYYY-MM-DD' in their zone). Part of "Little surprises" (one switch turns it all off). Tested in
   tests/app/surprises.test.ts. To change a door, change its line in DOORS. */

const p2 = (n: number) => String(n).padStart(2, '0');
const dow = (y: number, m: number, d: number) => new Date(Date.UTC(y, m - 1, d)).getUTCDay();
const ofDayNo = (n: number) => { const d = new Date(n * 86400000); return d.getUTCFullYear() + '-' + p2(d.getUTCMonth() + 1) + '-' + p2(d.getUTCDate()); };
const dayNo = (date: string) => Math.floor(Date.UTC(+date.slice(0, 4), +date.slice(5, 7) - 1, +date.slice(8, 10)) / 86400000);

/** the first Sunday of Advent: the 4th Sunday before Christmas (2026: Nov 29; 2027: Nov 28) */
export function adventSunday(y: number): string {
  return ofDayNo(dayNo(y + '-12-24') - dow(y, 12, 24) - 21);   // the Sunday on or before Dec 24 is the 4th Sunday of Advent
}
/** the four Advent Sundays of a year */
export const adventSundays = (y: number) => [0, 7, 14, 21].map(n => ofDayNo(dayNo(adventSunday(y)) + n));
/** candles lit on the wreath: 1-4 from the first Sunday of Advent to Dec 24, else 0 (no wreath) */
export function wreathCandles(date: string): number {
  const y = +date.slice(0, 4), first = adventSunday(y);
  if (date < first || date > y + '-12-24') return 0;
  return Math.min(4, 1 + Math.floor((dayNo(date) - dayNo(first)) / 7));
}

/** Dec 1-24: the day of December (the door to show), else 0 */
export const adventDay = (date: string) => date.slice(5, 7) === '12' && +date.slice(8, 10) <= 24 ? +date.slice(8, 10) : 0;
/** the countdown chip: "🎄 18 days to Christmas", "🎄 1 day to Christmas", Dec 24 "🎄 Christmas Eve!"; '' outside Dec 1-24 */
export function countdown(date: string): string {
  const d = adventDay(date); if (!d) return '';
  if (d === 24) return '🎄 Christmas Eve!';
  const n = 25 - d;
  return `🎄 ${n} day${n === 1 ? '' : 's'} to Christmas`;
}
/** may calendar door n (1-24) be opened on this date? From its own day until Christmas Day; later doors stay locked */
export function doorOpenable(n: number, date: string): boolean {
  if (n < 1 || n > 24 || date.slice(5, 7) !== '12') return false;
  const d = +date.slice(8, 10);
  return d <= 25 && n <= d;
}
/** the door chip and calendar show from Dec 1 to Christmas Day */
export const calendarDays = (date: string) => date.slice(5, 7) === '12' && +date.slice(8, 10) <= 25;
export const isChristmasDay = (date: string) => date.slice(5) === '12-25';
export const isStNicholas = (date: string) => date.slice(5) === '12-06';
/** the opening snowfall grows through December: flakes on that day (0: no snow) */
export function snowFlakes(date: string): number {
  if (date.slice(5, 7) !== '12') return 0;
  const d = +date.slice(8, 10);
  return d > 25 ? 0 : 10 + Math.round(Math.min(d, 24) * 26 / 24);   // Dec 1: 11 … Dec 24-25: 36
}
/** Christmas Eve evening (from 6 PM): a sleigh flies across the header once */
export const sleighTime = (wall: string) => wall.slice(5, 10) === '12-24' && +wall.slice(11, 13) >= 18;
/** working days (Monday-Friday) from this date through Dec 24 */
export function workdaysLeft(date: string): number {
  const y = +date.slice(0, 4); let n = 0;
  for (let d = dayNo(date), end = dayNo(y + '-12-24'); d <= end; d++) { const w = new Date(d * 86400000).getUTCDay(); if (w > 0 && w < 6) n++; }
  return n;
}

/** a door: text (trivia, a carol line, a winter fact, a work line; "{workdays}" = working days left from that door's
    day), or a little animation (with a caption) */
export interface Door { kind: 'trivia' | 'carol' | 'winter' | 'work' | 'anim'; text: string; anim?: 'snowman' | 'gingerbread' | 'candycane' | 'candy' }
export const DOORS: Door[] = [
  {kind: 'trivia', text: 'Decorated Christmas trees are usually traced to Germany in the 1500s. The tradition reached America with German immigrants.'},
  {kind: 'carol', text: 'Deck the halls with boughs of holly, fa la la la la, la la la la!'},
  {kind: 'winter', text: 'Every snowflake has six sides, because of the way water molecules fit together as they freeze.'},
  {kind: 'work', text: "Only {workdays} working days left before Christmas. You've got this."},
  {kind: 'anim', anim: 'snowman', text: 'A snowman, built just for you.'},
  {kind: 'anim', anim: 'candy', text: 'St. Nicholas Day! Here is something sweet for your stocking.'},
  {kind: 'carol', text: 'Joy to the world! The Lord is come. Let earth receive her King.'},
  {kind: 'trivia', text: 'Legend says candy canes were bent into a shepherd’s crook in Germany in the 1600s, and handed out to keep children quiet in church.'},
  {kind: 'winter', text: 'The winter solstice is the shortest day of the year in the Northern Hemisphere. After it, the days grow longer again.'},
  {kind: 'work', text: 'Small steps every day. {workdays} working days to go.'},
  {kind: 'anim', anim: 'gingerbread', text: 'The gingerbread man says hi.'},
  {kind: 'carol', text: 'Silent night, holy night, all is calm, all is bright.'},
  {kind: 'trivia', text: '"Jingle Bells" was first published in 1857, under the name "One Horse Open Sleigh".'},
  {kind: 'winter', text: 'Snow is not really white. Each crystal is clear; together they scatter every color of light, which looks white.'},
  {kind: 'carol', text: 'Jingle bells, jingle bells, jingle all the way!'},
  {kind: 'work', text: '{workdays} working days left. Wrapping up the year, one entry at a time.'},
  {kind: 'anim', anim: 'candycane', text: 'A candy cane, spun fresh.'},
  {kind: 'trivia', text: 'Rudolph the Red-Nosed Reindeer first appeared in 1939, in a booklet written for a department store.'},
  {kind: 'carol', text: 'O Christmas tree, O Christmas tree, how lovely are your branches!'},
  {kind: 'winter', text: 'Reindeer are the only deer where both the males and the females grow antlers.'},
  {kind: 'trivia', text: 'The first Christmas card was sent in England in 1843, by Sir Henry Cole.'},
  {kind: 'carol', text: 'Hark! The herald angels sing, glory to the newborn King.'},
  {kind: 'work', text: 'Almost there! Make today count, then enjoy the holidays.'},
  {kind: 'carol', text: '’Twas the night before Christmas, when all through the house, not a creature was stirring, not even a mouse.'},
];
/** a door's text for its own day of that year */
export function doorText(n: number, year: number): string {
  const d = DOORS[n - 1]; if (!d) return '';
  const w = workdaysLeft(year + '-12-' + p2(n));
  return d.text.replace('{workdays}', String(w));
}
